import { FilterValues } from './TableFilter';

export const applyFilters = <T>(data: T[], filters: FilterValues): T[] => {
  return data.filter(item => {
    return Object.entries(filters).every(([key, value]) => {
      // Skip empty filters
      if (value === null || value === undefined || value === '' || 
          (Array.isArray(value) && value.length === 0)) {
        return true;
      }
      
      let itemValue = getNestedValue(item, key);
      
      // Special handling for prices array
      if (key === 'prices' && Array.isArray(itemValue) && itemValue.length > 0) {
        itemValue = itemValue[0]?.sabilyAmount || itemValue[0]?.amount || 0;
      }
      
      // Text filter
      if (typeof value === 'string') {
        if (typeof itemValue === 'string') {
          return itemValue.toLowerCase().includes(value.toLowerCase());
        }
        if (typeof itemValue === 'number') {
          return itemValue.toString().includes(value);
        }
        return false;
      }
      
      // Array filter (multiselect)
      if (Array.isArray(value)) {
        // Date range
        if (value.length === 2 && value[0] instanceof Date && value[1] instanceof Date) {
          const itemDate = new Date(itemValue);
          return itemDate >= value[0] && itemDate <= value[1];
        }
        
        // Number range
        if (value.length === 2 && (typeof value[0] === 'number' || typeof value[1] === 'number')) {
          const numValue = Number(itemValue);
          const minValue = value[0];
          const maxValue = value[1];
          
          if (minValue !== undefined && maxValue !== undefined) {
            return numValue >= minValue && numValue <= maxValue;
          } else if (minValue !== undefined) {
            return numValue >= minValue;
          } else if (maxValue !== undefined) {
            return numValue <= maxValue;
          }
          return false;
        }
        
        // Multi-select
        return value.includes(itemValue);
      }

      // Boolean filter
      if (typeof value === 'boolean') {
        return Boolean(itemValue) === value;
      }

      // Direct comparison
      return itemValue === value;
    });
  });
};

const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : null;
  }, obj);
};

export const getFilteredDataCount = <T>(data: T[], filters: FilterValues): number => {
  return applyFilters(data, filters).length;
};

export const exportFilteredData = <T>(data: T[], filters: FilterValues, filename: string): void => {
  const filteredData = applyFilters(data, filters);
  const csvContent = convertToCSV(filteredData);
  downloadCSV(csvContent, filename);
};

const convertToCSV = (data: any[]): string => {
  if (data.length === 0) return '';

  const headers = Object.keys(data[0]);
  const csvRows = [
    headers.join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        if (typeof value === 'string') {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',')
    )
  ];

  return csvRows.join('\n');
};

const downloadCSV = (csvContent: string, filename: string): void => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};